'use strict';

var KayleeBase = require('./KayleeBase');
var log = require('../log/Log').channel('KayleeAuth');
var sutl = require('../sutl/Sutl');

var KayleeAuth = {
    getAuthFromLocalStorage: getAuthFromLocalStorage,
    getAnonymousRemoteControlSessionId: getAnonymousRemoteControlSessionId,
    isAuthenticated: isAuthenticated,
    isAuthenticatedAsAnonymous: isAuthenticatedAsAnonymous,
    isAuthenticatedAsAnonymousRemoteControl: isAuthenticatedAsAnonymousRemoteControl,
    isAuthenticatedAsController: isAuthenticatedAsController,
    isAuthenticatedAsUser: isAuthenticatedAsUser,
    requestRemoteAuth: requestRemoteAuth,
    resetPassword: resetPassword,
    signInWithCustomToken: signInWithCustomToken,
    signInWithEmailAndPassword: signInWithEmailAndPassword,
    signOut: signOut,
};

function getAuthFromLocalStorage() {
    // Read Firebase auth data from IndexedDB. This function is used by
    // the anonymous remote control authenticator to verify that writing the
    // auth data to IndexedDB is complete before redirecting to remote control.
    return new Promise(function(resolve, reject) {
        var dump = {};
        var dbRequest = window.indexedDB.open('firebaseLocalStorageDb');
        dbRequest.onsuccess = function() {
            var db = dbRequest.result;
            var stores = ['firebaseLocalStorage'];
            var tx = db.transaction(stores);
            asyncForEach(
                stores,
                function(store, next) {
                    var req = tx.objectStore(store).getAll();
                    req.onsuccess = function() {
                        dump[store] = req.result;
                        next();
                    };
                    req.onerror = function() {
                        reject(req.result);
                    };
                },
                function() {
                    resolve(dump);
                }
            );
        };
        dbRequest.onerror = function() {
            reject(dbRequest.result);
        };

        function asyncForEach(array, callback, done) {
            return runAndWait(0);

            function runAndWait(i) {
                if (i === array.length) return done();
                return callback(array[i], function() { runAndWait(i + 1); });
            }
        }
    });
}

function isAuthenticated() {
    var fbUser = KayleeBase.app.auth().currentUser;
    return fbUser !== null &&
        typeof fbUser !== 'undefined'; // cloud functions and admin don't support currentUser
}

function isAuthenticatedAsAnonymous(fbUser) {
    if (typeof fbUser === 'undefined') fbUser = KayleeBase.app.auth().currentUser;
    return fbUser !== null &&
        typeof fbUser !== 'undefined' && // cloud functions and admin don't support currentUser
        fbUser.isAnonymous;
}

function isAuthenticatedAsAnonymousRemoteControl(fbUser) {
    if (typeof fbUser === 'undefined') fbUser = KayleeBase.app.auth().currentUser;
    return fbUser !== null &&
        typeof fbUser !== 'undefined' && // cloud functions and admin don't support currentUser
        fbUser.providerId === 'firebase' &&
        !fbUser.isAnonymous &&
        fbUser.uid === 'anonymousRemoteControl';
}

function isAuthenticatedAsController(fbUser) {
    if (typeof fbUser === 'undefined') fbUser = KayleeBase.app.auth().currentUser;
    return fbUser !== null &&
        typeof fbUser !== 'undefined' && // cloud functions and admin don't support currentUser
        fbUser.providerId === 'firebase' &&
        !fbUser.isAnonymous &&
        fbUser.uid !== 'anonymousRemoteControl' &&
        Array.isArray(fbUser.providerData) &&
        fbUser.providerData.length === 0;
}

function isAuthenticatedAsUser(fbUser) {
    if (typeof fbUser === 'undefined') fbUser = KayleeBase.app.auth().currentUser;
    return fbUser !== null &&
        typeof fbUser !== 'undefined' && // cloud functions and admin don't support currentUser
        fbUser.providerId === 'firebase' &&
        !fbUser.isAnonymous &&
        (
            Array.isArray(fbUser.providerData) &&
            fbUser.providerData.length > 0 &&
            fbUser.providerData[0].providerId === 'password'
            ||
            fbUser.uid === 'anonymousRemoteControl'
        );
}

function requestRemoteAuth(code) {
    return KayleeBase.request('remoteAuth', { code: code })
    .then(function(resp) {
        log.debug('remote auth response = ' + JSON.stringify(resp, null, 4));

        // If the user is not logged in, Jayne will return a token for the anonymousRemoteControl
        // user. We must sign in with that token before proceeding to the remote control.

        if (resp.token) {
            return signInWithCustomToken(resp.token)
            .then(function() { return resp; });
        }

        return resp;
    })
    .then(function(resp) {
        // TODO: remove debugging messages

        var user = KayleeBase.app.auth().currentUser;
        log.debug('currentUser.uid = ' + user.uid);
        log.debug('currentUser.metadata = ' + JSON.stringify(user.metadata, null, 4));
        log.debug('currentUser.providerData = ' + JSON.stringify(user.providerData, null, 4));

        user.getIdTokenResult()
        .then(function(idTokenResult) {
            log.debug('currentUser.idTokenResult.claims = ' + JSON.stringify(idTokenResult.claims, null, 4));
        });

        return resp.roomId;
    });
}

function getAnonymousRemoteControlSessionId() {
    var user = KayleeBase.app.auth().currentUser;

    if (!isAuthenticatedAsAnonymousRemoteControl(user)) return Promise.resolve(null);

    return user.getIdTokenResult()
    .then(function(idTokenResult) {
        return sutl.getnode(idTokenResult, 'claims.sessionId');
    });
}

function resetPassword(email) {
    return KayleeBase.app.auth().sendPasswordResetEmail(email);
}

function signInWithCustomToken(token) {
    return internalSignInWithCustomToken(token, 0);
}

function internalSignInWithCustomToken(token, numRetries) {
    return KayleeBase.app.auth().signInWithCustomToken(token)
    .then(function(fbUser) {
        return Promise.resolve(fbUser);
    }, function(err) {
        if (numRetries >= 2 ||
            err.code === 'auth/custom-token-mismatch' ||
            err.code === 'auth/invalid-custom-token') {
            return Promise.reject(err);
        }

        log.warning('authentication failed with error: ' + JSON.stringify(err, null, 4));
        log.warning('retrying authentication, attempt: ' + (numRetries + 2));
        return internalSignInWithCustomToken(token, numRetries + 1);
    });
}

function signInWithEmailAndPassword(email, password) {
    return internalSignInWithEmailAndPassword(email, password, 0);
}

function internalSignInWithEmailAndPassword(email, password, numRetries) {
    return KayleeBase.app.auth().signInWithEmailAndPassword(email, password)
    .then(function(fbUser) {
        return Promise.resolve(fbUser);
    }, function(err) {
        if (numRetries >= 2 ||
            err.code === 'auth/invalid-email' ||
            err.code === 'auth/user-disabled' ||
            err.code === 'auth/user-not-found' ||
            err.code === 'auth/wrong-password') {
            return Promise.reject(err);
        }

        log.warning('authentication failed with error: ' + JSON.stringify(err, null, 4));
        log.warning('retrying authentication, attempt ' + (numRetries + 2));
        return internalSignInWithEmailAndPassword(email, password, numRetries + 1);
    });
}

function signOut() {
    return KayleeBase.app.auth().signOut();
}

module.exports = KayleeAuth;
